import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStickyMobileOffsetRef } from "@/hooks/useStickyMobileOffset";
import { useLocale } from "@/hooks/useLocale";
import type { Locale } from "@/i18n/types";
import { trackEvent } from "@/lib/analytics/events";
import {
  FloatingPriority,
  useFloatingCtaSlot,
  useGenericStickyAllowed,
} from "@/contexts/FloatingCtaContext";

interface StickyCtaBarProps {
  message?: string;
  primaryAction?: {
    label: string;
    href: string;
  };
  showWhatsApp?: boolean;
  showPhone?: boolean;
}

const DEFAULT_MESSAGES: Record<Locale, string> = {
  es: "Consulta 10 min gratis · Respuesta <2 h",
  de: "10-Min-Beratung gratis · Antwort <2 h",
  en: "Free 10-min consult · Reply <2 h",
  fr: "Consultation 10 min gratuite · Réponse <2 h",
  it: "Consulenza 10 min gratis · Risposta <2 h",
  pt: "Consulta 10 min grátis · Resposta <2 h",
};

const StickyCtaBar = ({
  message,
  primaryAction = { label: "Contactar", href: "/contacto" },
  showWhatsApp = true,
  showPhone = true,
}: StickyCtaBarProps) => {
  const locale = useLocale();
  const resolvedMessage = message ?? DEFAULT_MESSAGES[locale];
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const routeAllowed = useGenericStickyAllowed();

  const isVisible = useFloatingCtaSlot(
    "bottom-bar",
    FloatingPriority.GENERIC_STICKY,
    routeAllowed && isScrolled && !isDismissed
  );

  const stickyRef = useStickyMobileOffsetRef<HTMLDivElement>(isVisible);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = scrollY / (documentHeight - windowHeight);
      setIsScrolled(scrollPercentage > 0.3);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleWhatsApp = () => {
    trackEvent({ category: "retention", action: "sticky_wa_click", label: "sticky_bar" });
    window.open(
      "https://wa.me/41797700342?text=Hola, me gustaría obtener información sobre sus servicios",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handlePhone = () => {
    trackEvent({ category: "retention", action: "sticky_call_click", label: "sticky_bar" });
    window.location.href = "tel:+41797700342";
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={stickyRef}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
        >
          <div className="bg-background/95 backdrop-blur-lg border-t border-border p-4 shadow-lg">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-foreground flex-1 truncate">
                {resolvedMessage}
              </p>
              
              <div className="flex items-center gap-2">
                {showPhone && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePhone}
                    className="h-9 w-9 p-0"
                  >
                    <Phone className="h-4 w-4" />
                  </Button>
                )}
                
                {showWhatsApp && (
                  <Button
                    size="sm"
                    onClick={handleWhatsApp}
                    className="bg-green-600 hover:bg-green-700 h-9 w-9 p-0"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </Button>
                )}
                
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsDismissed(true)}
                  className="h-9 w-9 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StickyCtaBar;
